<script setup lang="ts">
import DeleteCampaign from '@/campaign/components/DeleteCampaign/DeleteCampaign.vue';
import CreateCampaign from '@/campaign/components/CreateCampaign/CreateCampaign.vue';
import CsvImport from '@/csv/components/CsvImport/CsvImport.vue';
import DncImport from '@/dnc/components/DncImport/DncImport.vue';
import LaunchCampaign from '@/campaign/components/LaunchCampaign/LaunchCampaign.vue';
import EndCampaign from '@/campaign/components/EndCampaign/EndCampaign.vue';
import SetCampaignDraft from '@/campaign/components/SetCampaignDraft/SetCampaignDraft.vue';
import ArchiveCampaign from '@/campaign/components/ArchiveCampaign/ArchiveCampaign.vue';
import DeleteCsv from '@/csv/components/DeleteCsv/DeleteCsv.vue';
import DncDelete from '@/dnc/components/DncDelete/DncDelete.vue';
import DncRecordDelete from '@/dnc/components/DncRecordDelete/DncRecordDelete.vue';
import DncRename from '@/dnc/components/DncRename/DncRename.vue';
import { onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import type { ModalMeta } from './modals';
import { getModalByString, initModal, MODAL, parseModalMeta } from './modals';
import NotImplemented from './NotImplemented.vue';
import { DATA_QA } from './modal-wrapper.constants';
import logger from '@/logger';
import { CampaignPatchActionEnum } from '@/apis/generated/dialbunny';
import CsvRename from '@/csv/components/CsvRename/CsvRename.vue';
import CallTimesSetting from '@/campaign/components/CampaignEdit/CallTimesSettingModal/CallTimesSettingModal.vue';
import DisableLocalDncModal from '@/campaign/components/DisableLocalDnc/DisableLocalDnc.vue';

const log = logger.getChild('ModalWrapper');
log.debug('Mounting');
onMounted(() => {
  log.debug('Mounted');
});

const router = useRouter();
initModal(router);

const modal = ref<MODAL | null>(null);
const meta = ref<ModalMeta | null>(null);

watch(
  router.currentRoute,
  // eslint-disable-next-line max-statements
  (newValue, oldValue) => {
    if (oldValue?.query.modal !== newValue.query.modal) {
      const modalFromQuery = newValue.query.modal;
      const metaFromQuery = newValue.query.modalMeta;
      if (!modalFromQuery || Array.isArray(modalFromQuery)) {
        log.debug('No modal in query. Closing modal');
        modal.value = null;
        meta.value = null;
        return;
      }

      const parsedMeta = parseModalMeta(metaFromQuery);
      if (metaFromQuery && !parsedMeta) {
        log.error(`Invalid modal meta value: ${metaFromQuery}`);
        modal.value = null;
        meta.value = null;
        return;
      }
      const modalToOpen = getModalByString(modalFromQuery);
      if (!modalToOpen) {
        log.error(`Invalid modal value: ${modalFromQuery}`);
      }

      log.debug('Opening modal', modalToOpen, parsedMeta);
      modal.value = modalToOpen;
      meta.value = parsedMeta;
    }
  },
  { immediate: true },
);
</script>
<template>
  <div v-if="modal !== null" :data-qa="DATA_QA.ROOT">
    <CreateCampaign v-if="modal === MODAL.CREATE_CAMPAIGN" :meta="meta" />
    <LaunchCampaign v-else-if="modal === MODAL.LAUNCH_CAMPAIGN" :meta="meta" />
    <EndCampaign v-else-if="modal === MODAL.END_CAMPAIGN" :meta="meta" />
    <DeleteCampaign v-else-if="modal === MODAL.DELETE_CAMPAIGN" :meta="meta" />
    <SetCampaignDraft
      v-else-if="modal === MODAL.SET_CAMPAIGN_DRAFT"
      :meta="meta"
    />
    <ArchiveCampaign
      v-else-if="modal === MODAL.ARCHIVE_CAMPAIGN"
      :action="CampaignPatchActionEnum.Archive"
      :meta="meta"
    />
    <ArchiveCampaign
      v-else-if="modal === MODAL.UNARCHIVE_CAMPAIGN"
      :action="CampaignPatchActionEnum.Unarchive"
      :meta="meta"
    />
    <CsvImport v-else-if="modal === MODAL.IMPORT_CSV" />
    <DncImport v-else-if="modal === MODAL.IMPORT_DNC" />
    <DeleteCsv v-else-if="modal === MODAL.DELETE_CSV" :meta="meta" />
    <CsvRename v-else-if="modal === MODAL.RENAME_CSV" :meta="meta" />
    <DncDelete v-else-if="modal === MODAL.DELETE_DNC" :meta="meta" />
    <DncRecordDelete
      v-else-if="modal === MODAL.DELETE_DNC_RECORD"
      :meta="meta"
    />
    <DncRename v-else-if="modal === MODAL.RENAME_DNC" :meta="meta" />
    <CallTimesSetting
      v-else-if="modal === MODAL.CALL_TIMES_SETTING"
      :meta="meta"
    />
    <DisableLocalDncModal
      v-else-if="modal === MODAL.DISABLE_LOCAL_DNC"
      :meta="meta"
    />
    <NotImplemented v-else-if="modal === MODAL.NOT_IMPLEMENTED" />
  </div>
</template>
